import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ChallengeBoard from "@/components/challenges/ChallengeBoard";
import ChallengeJoinFlow from "@/components/challenges/ChallengeJoinFlow";
import { Button } from "@/components/ui/button";
import { ArrowDown, Lightbulb } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import ErrorBoundary from "@/components/ErrorBoundary";

const Challenges: React.FC = () => {
  const location = useLocation();
  const boardRef = useRef<HTMLDivElement>(null);
  const [submitDialogOpen, setSubmitDialogOpen] = React.useState(false);
  
  // Join flow state for incoming navigation
  const [joinFlowOpen, setJoinFlowOpen] = useState(false);
  const [featuredChallenge, setFeaturedChallenge] = useState<{
    id: string;
    title: string;
    event_id: string | null;
  } | null>(null);
  const [preRegistrationId, setPreRegistrationId] = useState<string | null>(null);

  // Check for incoming state from registration flow
  useEffect(() => {
    const state = location.state as { 
      openJoinFlow?: boolean; 
      registrationId?: string;
    } | null;
    
    if (state?.openJoinFlow) {
      setPreRegistrationId(state.registrationId || null);
      fetchFeaturedChallenge();
      // Clear state to prevent re-triggering on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchFeaturedChallenge = async () => {
    const { data } = await supabase
      .from("challenges")
      .select("id, title, event_id")
      .eq("is_featured", true)
      .limit(1)
      .maybeSingle();
    
    if (data) {
      setFeaturedChallenge(data);
      setJoinFlowOpen(true);
    }
  };

  const scrollToBoard = () => {
    boardRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const canonicalUrl = `https://buildwithchrist.ai${location.pathname}`;

  return (
    <>
      <Helmet>
        <title>Challenge Repository | Build With Christ</title>
        <meta 
          name="description" 
          content="Discover open source challenges for the faith-tech community. Claim a challenge, build a team, and track progress together." 
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Hero Section - Action-oriented */}
        <section className="py-16 md:py-20 px-4 bg-gradient-to-b from-primary/5 to-transparent">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 tracking-tight">
              Find a Challenge.<br />
              <span className="text-primary">Build Something Meaningful.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join a community of builders creating technology that serves the Church.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button 
                size="lg" 
                className="gap-2 text-base px-6"
                onClick={scrollToBoard}
              >
                Browse Open Challenges
                <ArrowDown className="h-4 w-4" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="gap-2 text-base px-6"
                onClick={() => setSubmitDialogOpen(true)}
              >
                <Lightbulb className="h-4 w-4" />
                Submit Your Idea
              </Button>
            </div>
          </div>
        </section>

        {/* Challenge Board */}
        <div ref={boardRef}>
          <ErrorBoundary>
            <ChallengeBoard 
              submitDialogOpen={submitDialogOpen}
              onSubmitDialogChange={setSubmitDialogOpen}
            />
          </ErrorBoundary>
        </div>
      </div>

      {/* Join Flow Dialog for incoming navigation */}
      {featuredChallenge && (
        <ChallengeJoinFlow
          open={joinFlowOpen}
          onOpenChange={setJoinFlowOpen}
          challengeId={featuredChallenge.id}
          eventId={featuredChallenge.event_id}
          challengeTitle={featuredChallenge.title}
          initialAction="join"
          startAtStep={2}
          preRegistrationId={preRegistrationId || undefined}
        />
      )}
    </>
  );
};

export default Challenges;
